export interface InfraHubRuntimeConfig {
  readonly schemaVersion: 1;
  readonly webUrl: string;
  readonly apiBaseUrl: string;
  readonly oauth: {
    readonly authorizationEndpoint: string;
    readonly tokenEndpoint: string;
    readonly revocationEndpoint: string;
    readonly logoutEndpoint: string;
    readonly clientId: string;
    readonly redirectUri: string;
    readonly postLogoutRedirectUri: string;
    readonly scopes: readonly string[];
  };
}

type UnknownRecord = Readonly<Record<string, unknown>>;

function asRecord(value: unknown, label: string): UnknownRecord {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${label} must be an object.`);
  }
  return value as UnknownRecord;
}

function requiredString(record: UnknownRecord, key: string, label: string): string {
  const value = record[key];
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${label}.${key} must be a non-empty string.`);
  }
  return value;
}

function httpsUrl(value: string, label: string): string {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.hash.length > 0) {
    throw new Error(`${label} must be an HTTPS URL without a fragment.`);
  }
  return url.toString();
}

export function parseInfraHubRuntimeConfig(value: unknown): InfraHubRuntimeConfig {
  const root = asRecord(value, 'Runtime config');
  const allowedRoot = new Set(['schemaVersion', 'webUrl', 'apiBaseUrl', 'oauth']);
  const unknownRoot = Object.keys(root).filter((key) => !allowedRoot.has(key));
  if (unknownRoot.length > 0) {
    throw new Error(`Runtime config contains unknown fields: ${unknownRoot.join(', ')}.`);
  }
  if (root['schemaVersion'] !== 1) {
    throw new Error('Runtime config schemaVersion must be 1.');
  }

  const oauth = asRecord(root['oauth'], 'Runtime config.oauth');
  const allowedOauth = new Set([
    'authorizationEndpoint',
    'tokenEndpoint',
    'revocationEndpoint',
    'logoutEndpoint',
    'clientId',
    'redirectUri',
    'postLogoutRedirectUri',
    'scopes',
  ]);
  const unknownOauth = Object.keys(oauth).filter((key) => !allowedOauth.has(key));
  if (unknownOauth.length > 0) {
    throw new Error(`Runtime config.oauth contains unknown fields: ${unknownOauth.join(', ')}.`);
  }

  const scopes = oauth['scopes'];
  if (
    !Array.isArray(scopes) ||
    scopes.length === 0 ||
    scopes.some((scope) => typeof scope !== 'string' || scope.trim().length === 0)
  ) {
    throw new Error('Runtime config.oauth.scopes must contain non-empty strings.');
  }

  const webUrl = httpsUrl(
    requiredString(root, 'webUrl', 'Runtime config'),
    'Runtime config.webUrl',
  );
  const redirectUri = httpsUrl(
    requiredString(oauth, 'redirectUri', 'Runtime config.oauth'),
    'Runtime config.oauth.redirectUri',
  );
  const postLogoutRedirectUri = httpsUrl(
    requiredString(oauth, 'postLogoutRedirectUri', 'Runtime config.oauth'),
    'Runtime config.oauth.postLogoutRedirectUri',
  );
  if (
    new URL(redirectUri).origin !== new URL(webUrl).origin ||
    new URL(postLogoutRedirectUri).origin !== new URL(webUrl).origin
  ) {
    throw new Error('OAuth return URLs must use the configured web origin.');
  }

  return {
    schemaVersion: 1,
    webUrl,
    apiBaseUrl: httpsUrl(
      requiredString(root, 'apiBaseUrl', 'Runtime config'),
      'Runtime config.apiBaseUrl',
    ),
    oauth: {
      authorizationEndpoint: httpsUrl(
        requiredString(oauth, 'authorizationEndpoint', 'Runtime config.oauth'),
        'Runtime config.oauth.authorizationEndpoint',
      ),
      tokenEndpoint: httpsUrl(
        requiredString(oauth, 'tokenEndpoint', 'Runtime config.oauth'),
        'Runtime config.oauth.tokenEndpoint',
      ),
      revocationEndpoint: httpsUrl(
        requiredString(oauth, 'revocationEndpoint', 'Runtime config.oauth'),
        'Runtime config.oauth.revocationEndpoint',
      ),
      logoutEndpoint: httpsUrl(
        requiredString(oauth, 'logoutEndpoint', 'Runtime config.oauth'),
        'Runtime config.oauth.logoutEndpoint',
      ),
      clientId: requiredString(oauth, 'clientId', 'Runtime config.oauth'),
      redirectUri,
      postLogoutRedirectUri,
      scopes: scopes as string[],
    },
  };
}

export async function loadInfraHubRuntimeConfig(
  fetchApi: typeof fetch = globalThis.fetch,
): Promise<InfraHubRuntimeConfig> {
  const response = await fetchApi('/config.json', {
    method: 'GET',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Runtime config request failed with HTTP ${response.status}.`);
  }
  return parseInfraHubRuntimeConfig(await response.json());
}
